import { type CloudFlowNodeType } from "@doitintl/cmp-models";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Alert, Button, List, ListItem, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";

import { CopyCodeBlock } from "../../../../../Components/CopyCodeBlock/CopyCodeBlock";
import { type NodeConfigs, type PermissionCheckResult, PermissionValidityStatus } from "../../../types";

export const PermissionCheckResults = ({
  nodeConfig,
  loading,
  result,
}: {
  nodeConfig: NodeConfigs<CloudFlowNodeType.ACTION>;
  loading: boolean;
  result: PermissionCheckResult;
}) => {
  const getConsoleLink = (): string => {
    if (nodeConfig.parameters?.provider === "AWS") {
      return `https://console.aws.amazon.com/cloudshell/home?account=${nodeConfig.parameters.configurationValues.accountId}`;
    }
    if (nodeConfig.parameters?.provider === "GCP") {
      const [type, id] = nodeConfig.parameters.configurationValues.serviceAccountResource.split("/");
      if (type === "projects") {
        return `https://console.cloud.google.com/iam-admin/iam?project=${id}`;
      }

      return `https://console.cloud.google.com/iam-admin/iam?organizationId=${id}`;
    }

    return "#";
  };

  if (loading) return null;

  switch (result.status) {
    case PermissionValidityStatus.VALID: {
      return (
        <Alert severity="success" sx={{ px: 2, alignSelf: "stretch" }}>
          <Typography variant="body2">All permissions are in place to run this action</Typography>
        </Alert>
      );
    }
    case PermissionValidityStatus.UNABLE_TO_VERIFY: {
      return (
        <Alert severity="warning" sx={{ px: 2, alignSelf: "stretch" }}>
          <Typography variant="body2">
            We couldn't verify the necessary permissions or no permission is associated with this action.
          </Typography>
        </Alert>
      );
    }
    case PermissionValidityStatus.INVALID: {
      if (Array.isArray(result.data)) {
        const validAccounts = result.data.filter((d) => d.hasPermission);
        const invalidAccounts = result.data.filter((d) => !d.hasPermission);

        return (
          <>
            <Typography variant="subtitle2" fontWeight={500}>
              Permission summary
            </Typography>
            {validAccounts.length > 0 && (
              <Stack direction="row" spacing={1}>
                <CheckCircleIcon sx={(theme) => ({ fill: theme.palette.success.main })} />
                <Stack>
                  <Typography variant="body2">
                    {validAccounts.length} {validAccounts.length > 1 ? "accounts have" : "account has"} correct
                    permissions:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {validAccounts.map((a) => a.accountName).join(", ")}
                  </Typography>
                </Stack>
              </Stack>
            )}
            {invalidAccounts.length > 0 && (
              <Stack direction="row" spacing={1}>
                <CancelIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                <Stack>
                  <Typography variant="body2">
                    {invalidAccounts.length} {invalidAccounts.length > 1 ? "accounts are" : "account is"} missing
                    permissions:
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {invalidAccounts.map((a) => a.accountName).join(", ")}
                  </Typography>
                </Stack>
              </Stack>
            )}
            <Alert severity="warning">
              <Typography variant="body2">We require the following permissions in order to run this node: </Typography>
              <List sx={{ listStyleType: "disc", pl: 2 }}>
                {[...new Set(invalidAccounts.flatMap((acc) => acc.requiredPermissions))].map((permission) => (
                  <ListItem key={permission} sx={{ display: "list-item", wordBreak: "break-all" }}>
                    {permission}
                  </ListItem>
                ))}
              </List>
            </Alert>
            <Typography variant="subtitle2" fontWeight={500}>
              Run the provided command in each target AWS account using AWS CloudShell or the CLI
            </Typography>
            {invalidAccounts.map((r) => (
              <Box key={r.accountName}>
                <Typography variant="body2" color="textSecondary" mb={1}>
                  {r.accountName}
                </Typography>
                <CopyCodeBlock base={r.command} />
              </Box>
            ))}
          </>
        );
      }

      return (
        <>
          <Alert severity="warning">
            <Typography variant="body2">We require the following permissions in order to run this node: </Typography>
            <List sx={{ listStyleType: "disc", pl: 2 }}>
              {result.data.requiredPermissions.map((permission) => (
                <ListItem key={permission} sx={{ display: "list-item", wordBreak: "break-all" }}>
                  {permission}
                </ListItem>
              ))}
            </List>
          </Alert>
          <Stack spacing={1} alignItems="flex-start">
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              Run the following command to grant the permission:
            </Typography>
            <CopyCodeBlock base={result.data.command} />
            <Button variant="outlined" component="a" href={getConsoleLink()} target="_blank">
              <OpenInNewIcon
                sx={{
                  width: 20,
                  height: 20,
                  mr: 1,
                }}
              />
              Go to {nodeConfig.parameters?.provider} Console
            </Button>
          </Stack>
        </>
      );
    }
    default:
      return null;
  }
};
